
export type GameType = string;

export enum ProductType {
  CARD = 'Carta Avulsa',
  SEALED = 'Produto Selado',
  ACCESSORY = 'Acessório',
  TICKET = 'Ingresso',
  PROMO = 'Promoção',
  STARTER_DECK = 'Starter Deck',
  BOOSTER = 'Booster Box',
  PREMIUM_BANDAI = 'Premium Bandai'
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  beauty_name?: string;
  description?: string;
  type?: string;
  product_type_id?: number | string;
  price?: number;
  mspr?: number;
  originalPrice?: number;
  imageUrl?: string;
  image_url?: string;
  card_back_url?: string;
  storeName?: string;
  storeId?: string | number;
  store_id?: string | number;
  storeHandle?: string;
  storeLogo?: string;
  isOfficialPartner?: boolean;
  is_official?: boolean;
  is_sponsored?: boolean;
  game?: GameType | string;
  game_id?: number | string;
  stock?: number;
  totalStock?: number;
  reservedStock?: number;
  release_date?: string;
  prerelease?: boolean;
  pre_release?: boolean;
  pre_sale?: boolean;
  ext_link?: string;
  weight?: number;
}

export const isProductPreSale = (product: any): boolean => {
  if (!product) return false;
  if (product.pre_release === true || product.pre_release === 'true' || product.pre_release === 1) return true;
  if (product.prerelease === true || product.prerelease === 'true' || product.prerelease === 1) return true;
  if (product.pre_sale === true || product.pre_sale === 'true' || product.pre_sale === 1) return true;
  if (product.is_presale === true || product.is_presale === 'true' || product.is_presale === 1) return true;
  if (product.is_prerelease === true || product.is_prerelease === 'true' || product.is_prerelease === 1) return true;
  if (product.isPreSale === true || product.isPreSale === 'true') return true;

  if (product.release_date) {
    const relTime = new Date(product.release_date).getTime();
    if (!isNaN(relTime) && relTime > Date.now()) {
      return true;
    }
  }

  if (Array.isArray(product.store_products)) {
    const hasPreSaleStore = product.store_products.some((sp: any) => 
      !sp.deleted_at && (sp.pre_sale === true || sp.pre_sale === 'true' || sp.pre_sale === 1)
    );
    if (hasPreSaleStore) return true;
  }

  return false;
};

export const isTicketProduct = (product: any): boolean => {
  if (!product) return false;
  const typeData = Array.isArray(product.product_types) ? product.product_types[0] : product.product_types;
  const catId = Number(typeData?.category_id || product.category_id || product.product_category_id || 0);
  const typeId = Number(product.product_type_id || typeData?.id || 0);
  const typeName = (typeData?.name || product.product_type || product.type || '').toLowerCase();
  const catName = (product.category?.name || product.categories?.name || '').toLowerCase();
  const catSlug = (product.category?.slug || product.categories?.slug || '').toLowerCase();
  const pSlug = (product.slug || '').toLowerCase();

  if (
    catId === 3 ||
    typeId === 52 ||
    typeId === 3 ||
    typeName === 'ticket' ||
    typeName === 'ingresso' ||
    typeName === 'inscrição' ||
    typeName === 'inscricao' ||
    catName === 'ingresso' ||
    catName === 'ingressos' ||
    catSlug === 'ticket' ||
    catSlug === 'tickets' ||
    catSlug === 'ingresso' ||
    catSlug === 'ingressos' ||
    pSlug.startsWith('inscricao-') ||
    pSlug.startsWith('ingresso-') ||
    pSlug.startsWith('ticket-') ||
    product.is_ticket === true ||
    product.isTicket === true
  ) {
    return true;
  }

  if (Array.isArray(product.tournament_tickets) && product.tournament_tickets.length > 0) {
    return true;
  }

  return false;
};

export interface UserAddress {
  id: string;
  user_id: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  cep: string;
  is_default: boolean;
  label?: string;
  country?: string;
  reference?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CartItem extends Product {
  quantity: number;
  productId?: string | number;
}

export interface OrderComment {
  user: string;
  text: string;
  timestamp: string;
  isAdmin?: boolean;
}

export interface Order {
  id: string;
  date: string;
  status: 'Pendente' | 'Enviado' | 'Entregue' | 'Cancelado';
  total: number;
  shippingCost: number;
  storeName: string;
  paymentMethod: 'Mercado Pago' | 'Pix Direto' | 'A combinar com vendedor';
  couponUsed: string | null;
  comments: OrderComment[];
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
}

export interface StoreEvent {
  id: string;
  name: string;
  date: string;
  price?: number;
  totalSpots: number;
  filledSpots: number;
  type: 'Tournament' | 'Prerelease' | 'Special';
  game: GameType;
  description?: string;
  imageUrl?: string;
  isHighlighted?: boolean;
}

export interface Ticket {
  id: string;
  eventName: string;
  date: string;
  location: string;
  qrCode: string;
  status: 'Válido' | 'Utilizado' | 'Expirado';
}

export interface DbCard {
  id: number;
  external_id: string;
  game_id: number;
  name: string;
  image_url: string | null;
  price: number | null;
  variant_number: number;
  price_updated_at: string | null;
  set_code: string | null;
  rarity: string | null;
  is_foil: boolean;
  variant_external_id: string | null;
  variant_code: string | null;
  tcgplayer_id: string | null;
}

export interface Card {
  id: string;
  code: string;
  name: string;
  imageUrl: string;
  cardBackUrl?: string;
  card_back_url?: string;
  game: GameType;
  game_id?: string | number;
  set: string;
  variants?: any[];
  rarity?: string;
  type?: string;
  price?: number;
  variant_number?: number;
  is_foil?: boolean;
  variant_external_id?: string | null;
  variant_code?: string | null;
  tcgplayer_id?: string | null;
  tcgplayerId?: string | number | null;
  tcgPlayerId?: string | number | null;
  description?: string;
  effect?: string;
  oracle_text?: string;
  desc?: string;
  ability?: string;
  cost?: any;
  playCost?: any;
  energyCost?: any;
  cmc?: any;
  level?: any;
  power?: any;
  atk?: any;
  bp?: any;
  dp?: any;
  hp?: any;
  defense?: any;
  def?: any;
  color?: any;
  colors?: any;
  attribute?: any;
  element?: any;
  domain?: any;
  family?: any;
  race?: any;
  trait?: any;
  traits?: any;
  subtypes?: any;
  types?: any;
  form?: any;
  [key: string]: any;
}

export interface DeckCard {
  card: Card;
  quantity: number;
}

export interface Deck {
  id: string;
  name: string;
  game: GameType;
  format: string;
  cards: {
    main: DeckCard[];
    extra?: DeckCard[];
    eggs?: DeckCard[];
  };
}

export interface Store {
  id: string | number;
  name: string;
  slug: string;
  city?: string;
  neighborhood?: string;
  address?: string;
  zone?: string;
  logo?: string;
  site?: string;
  is_partner?: boolean;
  contact?: any;
  social_links?: any;
  created_at?: string;
  updated_at?: string;
  sales_enabled: boolean;
  order_expiration_hours?: number;
  about?: string;
  state_id: number | string;
  // Legacy / Derived
  location?: string;
  isPartner?: boolean;
  whatsapp?: string;
  phone?: string;
  email?: string;
  schedule?: Array<{
    game: GameType;
    day: string;
    time: string;
    fee?: string;
    tipo?: string;
  }>;
  businessHours?: Array<{
    day_of_week: number;
    open_time: string;
    close_time: string;
    is_closed: boolean;
  }>;
  isOpen?: boolean;
  events?: StoreEvent[];
  instagram?: string;
  discord?: string;
  opening_hours?: string;
}

export interface UpdateLog {
  date: string;
  changes: string[];
}

export interface UserAction {
  user: string;
  userId?: string;
  action: string;
  target: string;
  timestamp: string;
  avatar?: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: string;
  read: boolean;
}

export interface Trade {
  id: string;
  partner: {
    name: string;
    avatar: string;
  };
  status: 'Pendente' | 'Aceito' | 'Recusado' | 'Concluído';
  offering: Card[];
  requesting: Card[];
  timestamp: string;
}

export interface Tournament {
  id: string;
  name: string;
  game: GameType;
  date: string;
  location: string;
  price?: number;
  totalSpots: number;
  filledSpots: number;
  status: 'Aberto' | 'Em Andamento' | 'Finalizado';
  imageUrl: string;
}

export interface RankingItem {
  name: string;
  userId?: string;
  cards: number;
  rank: number;
  avatar: string;
  isTop?: boolean;
}

export interface ShippingMethod {
  id: number;
  name: string;
  description?: string;
  price?: number;
  estimated_days?: number;
  delivery_days?: number | null;
  company_name?: string | null;
  external_id?: number | null;
  company_id?: number | null;
  service_type?: string | null;
  source?: string | null;
  is_better_envio?: boolean | null;
  shipping_method_id?: number | null;
}

export interface PaymentMethod {
  id: string | number;
  name: string;
  description?: string;
  type?: string;
  slug?: string;
  requires_online?: boolean;
}

export interface UserProfile {
  id: string | number;
  email?: string;
  email_copy?: string;
  username: string;
  fullname?: string;
  avatar?: string;
  photoURL?: string;
  bio?: string;
  phone?: string;
  birth_date?: string;
  fav_game_id?: number | string;
  favorite_game?: string;
  favorite_cardgame_id?: number | string;
  fighter_tags?: string | string[];
  banner_url?: string;
  cover_url?: string;
  gender?: string;
  role?: string;
  vip?: boolean;
  bandai_member?: string;
}

export const FIGHTER_TAGS = [
  'Casual', 'Competitivo', 'Colecionador', 'Trabalhador', 'Juiz', 
  'Streamer', 'Vendedor', 'Pro Player', 'Batedor de Bafo', 'Mestre de Deck'
];

export const PREDEFINED_AVATARS = [
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114474/woEyRVKRhWojYuIlGcOD--0--xtw18_z3vee9.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761957942/i6stfajJfAvGdB5sPGkz--0--x1tvd_tesfo4.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761957944/xlqPvNUixJYJkF41njQR--0--5kbnx_zytmy1.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761957939/k5szIZAg1U6ytSDL0aZl--0--skv9i_xfctmb.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114462/6gcrLOypNE8I8WY428H0--0--0499y_hwig5i.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955256/WEhyhhCcgFr5t9tnIUKL--0--kfsjq_cwdm7o.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955251/JuHbjfGg9ALZJPyNa4gT--0--1w02j_dj7skr.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955251/H1yFuolXAEMMJjNaDhuR--0--pses9_o5d7ys.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114461/0ClkdZniiJKYLVaraamN--0--lb9kc_dnzsit.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114473/tPpka7gY2urnt7ACmMcr--0--91ibu_xmsczn.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761957939/8pHkBGAKmXxMFWcYh93O--0--ocxw6_licxyn.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761957940/VSEzZGHz2M3Q12ZxviF9--0--mq6ar_zjjyal.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955257/zg73BAw5HJbdB5mptokN--0--m5roy_ab9mdm.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955250/1wPRZYN91F0MB4H6Gfyx--0--vi2hd_pw185x.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955250/7ABBB46gTYjaYNK1KW7O--0--wc4j8_b6fogi.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114467/M0KAWc0Ge3WgorGWxuBH--0--uusxj_hcf6j1.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955250/D6acCxh3r3rg2fCBsWWg--0--cris9_txqed4.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114476/wQdSHVgNZR0jQxtukfI8--0--xnu42_laqlwe.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114471/RnABmUzz3lE2trQ3dO76--0--0mqpg_dz0xmq.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762115514/44ZmWWgbHVtMjQ81B3wr--0--tr1bl_swuqfk.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762115513/9o1gm2iHzGmwImTv9hNu--0--xwij1_wky6dl.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1761955256/TfNST6mFd88uBdIrcQqd--0--6rary_rj6tcb.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762115516/dSvrMMd8gY5mm242bGcf--0--d9p8g_dmhhti.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114478/wqG1MaaSmRJX8wD55dM7--0--m7cyy_rbgkx4.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114463/hHrUZ6ReF5vTaW1gBA5N--0--bvvqf_sdfx3o.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114465/JWUC3Fr6OJza1JNbqlpn--0--un12g_n44j90.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1762114469/ODeZPRjqyECN0qRpJjZU--0--9ypju_muoohd.jpg",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1789763271/profpic01.webp",
  "https://res.cloudinary.com/dtlp9ayj1/image/upload/v1789763274/profpic12.webp"
];

export const PREDEFINED_COVERS = [
  'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1606103920295-9a091573f160?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1534423861386-85a16f5d13fd?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1552820728-8b83bb6b773f?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1611996598518-d767f21d651f?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1549490349-8643362247b5?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?auto=format&fit=crop&q=80&w=1200',
  'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=1200'
];

